/* eslint-disable */
import { fetch_form_tree } from '@/services/main_section_service';
import {
  extract_items,
  first_value,
  normalize_text,
  parse_response_data
} from '@/services/onboarding_data_helpers';
import { normalize_main_section } from '@/services/onboarding_form_helpers';
import { fetch_application_forms } from '@/services/onboarding_journey_application_service';
import { fetch_all_onboarding_cases, fetch_onboarding_execution_status } from '@/services/onboarding_journey_service';

export function risk_debug(message, details = {}) {
  console.info(`[RISK FRONTEND DEBUG] ${String(message)}`, details);
}

export function normalize_risk_level(value) {
  const normalized = normalize_text(value);
  const match = find_risk_match(normalized, [
    { label: 'High', words: ['high'] },
    { label: 'Medium', words: ['medium', 'moderate'] },
    { label: 'Low', words: ['low'] }
  ]);
  return match?.label || get_unmatched_risk_level(value);
}

function get_unmatched_risk_level(value) {
  return value ? String(value) : 'Not assessed';
}

export function risk_tone_from_level(value) {
  const normalized = normalize_text(value);
  const match = find_risk_match(normalized, [
    { tone: 'high', words: ['high'] },
    { tone: 'medium', words: ['medium', 'moderate'] },
    { tone: 'low', words: ['low'] }
  ]);
  return match?.tone || 'pending';
}

function find_risk_match(normalized, matches) {
  return matches.find((match) => match.words.some((word) => normalized.includes(word)));
}

export function normalize_assessment(assessment = {}) {
  return {
    ...assessment,
    id: Number(first_value([
      assessment?.risk_framework_assessment_id,
      assessment?.risk_framework_assessment?.id,
      assessment?.id
    ], 0)) || null,
    entity_risk_framework_assessment_id: Number(first_value([
      assessment?.assessment_id,
      assessment?.entity_risk_framework_assessment,
      assessment?.entity_risk_framework_assessment_id
    ], 0)) || null,
    name: first_value([
      assessment?.risk_assessment,
      assessment?.assessment_name,
      assessment?.name
    ], 'Risk assessment'),
    subject_id: Number(first_value([assessment?.subject_id], 0)) || null,
    is_started: Boolean(assessment?.is_started),
    risk_level: first_value([assessment?.risk_level, assessment?.result?.risk_level], null),
    total_score: first_value([assessment?.total_score, assessment?.result?.total_score], null)
  };
}

export function extract_status_assessments(onboarding_status = {}) {
  const status_payload = onboarding_status?.data && typeof onboarding_status.data === 'object'
    ? onboarding_status.data
    : onboarding_status;
  const items = resolve_risk_assessment_items(status_payload?.risk_assessments);

  return items.map(normalize_assessment).filter((assessment) => assessment.id);
}

function resolve_risk_assessment_items(risk_state) {
  if (Array.isArray(risk_state?.items)) return risk_state.items;
  if (Array.isArray(risk_state)) return risk_state;
  return [];
}

export function is_child_onboarding_case(case_item = {}) {
  const current_entity_id = Number(first_value([
    case_item?.current_entity?.id,
    case_item?.current_entity
  ], 0));
  const parent_entity_id = Number(first_value([
    case_item?.entity?.id,
    case_item?.entity
  ], 0));

  return current_entity_id > 0 && parent_entity_id > 0 && current_entity_id !== parent_entity_id;
}

export function case_has_embedded_risk_result(case_item = {}) {
  const embedded_risk_level = get_case_risk_level(case_item);
  const embedded_risk_score = get_case_risk_score(case_item);

  return Boolean(String(embedded_risk_level || '').trim()) || embedded_risk_score !== null;
}

export function apply_embedded_risk_result(case_item = {}) {
  const raw_risk_level = get_case_risk_level(case_item);
  const risk_level = normalize_risk_level(raw_risk_level);
  const total_score = get_case_risk_score(case_item);

  return {
    ...case_item,
    risk: risk_level,
    risk_level,
    risk_score: total_score,
    risk_tone: risk_tone_from_level(risk_level),
    risk_result: case_item?.risk_result || {
      risk_level,
      total_score
    }
  };
}

function get_case_risk_level(case_item = {}) {
  return first_value([case_item?.risk_result?.risk_level, case_item?.risk_level, case_item?.risk_rating, case_item?.risk], '');
}
function get_case_risk_score(case_item = {}) {
  return first_value([
    case_item?.risk_result?.total_score,
    case_item?.risk_score,
    case_item?.risk_rating_score,
    case_item?.threshold_assessment?.score,
    case_item?.threshold_assessment?.risk_score
  ], null);
}
export function normalize_submission_result(result, assessment) {
  return {
    ...result,
    assessment_id: assessment.id,
    assessment_name: assessment.name,
    risk_level: normalize_risk_level(result.risk_level),
    risk_tone: risk_tone_from_level(result.risk_level)
  };
}
export function apply_latest_risk_result(case_item, latest_result, risk_level) {
  return {
    ...case_item,
    risk: risk_level,
    risk_level,
    risk_score: latest_result.total_score,
    risk_tone: risk_tone_from_level(risk_level),
    risk_result: latest_result
  };
}

async function find_case_for_application(application_id) {
  const cases = await fetch_all_onboarding_cases().catch(() => []);
  return cases.find((case_item) => Number(case_item?.application_id) === Number(application_id)) || null;
}

export async function resolve_case_context({
  application_id,
  entity_profile_id = null,
  entity_type_id = null,
  project_id = null,
  selected_form = {}
} = {}) {
  const known_entity_profile_id = resolve_known_entity_profile_id(entity_profile_id, selected_form);
  const known_entity_type_id = resolve_known_entity_type_id(entity_type_id, selected_form);
  const case_item = known_entity_profile_id && known_entity_type_id
    ? null
    : await find_case_for_application(application_id);
  const context = build_case_context({
    application_id,
    known_entity_profile_id,
    known_entity_type_id,
    project_id,
    case_item
  });
  risk_debug('case context resolved', context);
  return context;
}

function resolve_known_entity_profile_id(entity_profile_id, selected_form = {}) {
  return Number(first_value([
    entity_profile_id,
    selected_form?.entity_profile_id,
    selected_form?.entity_profile,
    selected_form?.entity?.id,
    selected_form?.entity_profile?.id
  ], 0)) || null;
}

function resolve_known_entity_type_id(entity_type_id, selected_form = {}) {
  return Number(first_value([
    entity_type_id,
    selected_form?.entity_type_id,
    selected_form?.entity_type?.id
  ], 0)) || null;
}

function build_case_context({
  application_id,
  known_entity_profile_id,
  known_entity_type_id,
  project_id,
  case_item
}) {
  const resolved_project_id = first_value([project_id, case_item?.project_id, case_item?.profile?.project_id], 0);
  return {
    application_id: Number(application_id || 0),
    entity_profile_id: Number(first_value([known_entity_profile_id, case_item?.entity_profile_id], 0)) || null,
    entity_type_id: Number(first_value([known_entity_type_id, case_item?.entity_type_id], 0)) || null,
    project_id: Number(resolved_project_id) || null,
    case_item
  };
}

export async function get_configured_assessments(context) {
  if (is_child_onboarding_case(context.case_item)) return [];
  const onboarding_status = await fetch_onboarding_status_for_context(context);

  const status_assessments = extract_status_assessments(onboarding_status);
  risk_debug('configured assessments resolved', {
    application_id: context.application_id,
    entity_profile_id: context.entity_profile_id,
    entity_type_id: context.entity_type_id,
    assessments: status_assessments,
    raw_risk_state: parse_response_data(onboarding_status)?.data?.risk_assessments
  });
  return status_assessments;
}

function has_runtime_scope(context) {
  return Boolean(context.entity_type_id && context.entity_profile_id && context.application_id);
}

async function fetch_onboarding_status_for_context(context) {
  if (!has_runtime_scope(context)) return null;

  return fetch_onboarding_execution_status({
    entity_type_id: context.entity_type_id,
    entity_profile_id: context.entity_profile_id,
    application_id: context.application_id,
    ...(context.project_id ? { project_id: context.project_id } : {})
  }).catch(() => null);
}

function normalize_answer_for_risk(answer) {
  if (Array.isArray(answer)) return answer.map((item) => String(item || '').trim()).filter(Boolean);
  const normalized = String(answer ?? '').trim();
  return normalized ? normalized : null;
}

function has_own_key(target, key) {
  return Boolean(target && Reflect.ownKeys(target).includes(key));
}

function add_question_to_answer_map(answer_map, question, live_values = null) {
  const question_id = Number(question?.question_id || 0);
  const answer = normalize_answer_for_risk(resolve_question_answer(question, live_values));
  if (question_id && answer) answer_map.set(question_id, answer);
}

function resolve_question_answer(question, live_values = null) {
  const key = String(question?.id);
  return live_values && has_own_key(live_values, key)
    ? Reflect.get(live_values, key)
    : question?.answer;
}

function add_form_questions_to_answer_map(answer_map, main_sections = [], live_values = null) {
  main_sections.forEach((main_section) => {
    (main_section?.sub_sections || []).forEach((sub_section) => {
      (sub_section?.questions || []).forEach((question) => {
        add_question_to_answer_map(answer_map, question, live_values);
      });
    });
  });
}

export const collect_form_answers_by_question_id = async ({ application_id, application_forms = [] }) => {
  const answer_map = new Map();
  const form_trees = await Promise.all(
    application_forms.map((form) => fetch_form_tree({
      application_id,
      application_form_id: Number(form?.application_form_id || form?.id || 0),
      ...(form?.entity_profile_id ? { entity_profile_id: form.entity_profile_id } : {})
    }).catch(() => null))
  );

  form_trees.forEach((response) => {
    const sections = (Array.isArray(response?.data) ? response.data : [])
      .map((item, index) => normalize_main_section(item, index));
    add_form_questions_to_answer_map(answer_map, sections);
  });

  return answer_map;
};

export const collect_current_form_answer_map = (current_form_sections = [], current_form_values = {}) => {
  const answer_map = new Map();
  add_form_questions_to_answer_map(answer_map, current_form_sections, current_form_values);
  return answer_map;
};

export const get_application_answer_map = async (application_id) => {
  const application_forms = await fetch_application_forms(application_id).catch(() => []);
  return collect_form_answers_by_question_id({ application_id, application_forms });
};

export const normalize_framework_items = (response) => extract_items(response)
  .map(normalize_assessment)
  .filter((assessment) => assessment.id);
